import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  Mail,
  Smartphone,
  Info,
  RefreshCw,
  Copy,
  Check,
  ShieldCheck,
  AlertTriangle,
  Zap,
  Key,
  Download,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/contexts/LanguageContext';
import { BrandLogo } from '@/components/BrandLogo';
import { AuthApi } from '../api/auth-api';

type Step = 'choose' | 'setup' | 'verify' | 'done';
type Method = 'app' | 'email';

const EMPTY_CODE = ['', '', '', '', '', ''];

const TwoStepConfigure: React.FC = () => {
  const { t, currentLanguage } = useLanguage();
  const { user } = useAuth();
  const authApi = new AuthApi();

  const initiallyActive = user?.twoFactorEnabled
    ? ((user.twoFactorType as Method | undefined) ?? null)
    : null;

  const [step, setStep] = useState<Step>('choose');
  const [method, setMethod] = useState<Method | null>(null);
  const [qrCode, setQrCode] = useState<string>('');
  const [secret, setSecret] = useState<string>('');
  const [code, setCode] = useState<string[]>(EMPTY_CODE);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [activated, setActivated] = useState<Method | null>(initiallyActive);
  const [resendIn, setResendIn] = useState(0);
  const [backupCopied, setBackupCopied] = useState(false);
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  // Coduri de rezervă mock — backend-ul nu le generează încă. Le regenerăm
  // doar o dată per sesiune (useMemo) ca să nu sară la fiecare re-render.
  const backupCodes = useMemo(() => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const block = () =>
      Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    return Array.from({ length: 10 }, () => `${block()}-${block()}`);
  }, []);

  // Countdown pentru retrimitere email (folosește pattern-ul din claude_design).
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const isRo = currentLanguage === 'ro';

  // Sync cu user din context
  useEffect(() => {
    if (user?.twoFactorEnabled) {
      setActivated((user.twoFactorType as Method | undefined) ?? null);
    } else {
      setActivated(null);
    }
  }, [user]);

  // Background claude-design cât e montată pagina (consecvent cu restul aplicației).
  useEffect(() => {
    document.body.classList.add('dashboard-claude-bg');
    return () => {
      document.body.classList.remove('dashboard-claude-bg');
    };
  }, []);

  const stepsList: { id: Step; label: string }[] = [
    { id: 'choose', label: t('tfa.steps.choose') },
    { id: 'setup', label: t('tfa.steps.setup') },
    { id: 'verify', label: t('tfa.steps.verify') },
    { id: 'done', label: t('tfa.steps.done') },
  ];

  const pickMethod = (m: Method) => {
    setError('');
    setMethod(m);
    if (m === 'app') {
      authApi.enableApp((response) => {
        const data = response?.data?.data || response?.data || {};
        setQrCode(data.qrCode || '');
        setSecret(data.secret || '');
        setStep('setup');
      });
    } else {
      setStep('setup');
    }
  };

  const handleSendEmail = () => {
    setEmailSending(true);
    authApi.enableEmail((_response) => {
      setEmailSending(false);
      // Trecem la pasul de verify (potrivit cu claude_design). Backend-ul
      // poate marca pe verify-setup contul ca activat — în fallback,
      // dacă verify e no-op pentru email, marcăm activat la handleVerify.
      setCode(EMPTY_CODE);
      setError('');
      setResendIn(30);
      setStep('verify');
    });
  };

  const handleResendEmail = () => {
    if (resendIn > 0) return;
    setCode(EMPTY_CODE);
    setError('');
    authApi.enableEmail(() => {
      setResendIn(30);
    });
  };

  const onCodeChange = (i: number, val: string) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...code];
    next[i] = val;
    setCode(next);
    setError('');
    if (val && i < 5) refs.current[i + 1]?.focus();
  };

  const onCodeKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[i] && i > 0) refs.current[i - 1]?.focus();
  };

  const onCodePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const next = [...EMPTY_CODE];
    for (let i = 0; i < text.length; i++) next[i] = text[i];
    setCode(next);
    refs.current[Math.min(text.length, 5)]?.focus();
  };

  const handleVerify = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (code.some((d) => !d)) {
      setError(t('tfa.err.all_digits'));
      return;
    }
    setVerifying(true);
    setError('');
    authApi.verify(
      code.join(''),
      (_response) => {
        setVerifying(false);
        setActivated(method || 'app');
        setStep('done');
      },
      (errMsg) => {
        setVerifying(false);
        setError(errMsg);
      },
    );
  };

  const copyBackupCodes = async () => {
    try {
      await navigator.clipboard.writeText(backupCodes.join('\n'));
      setBackupCopied(true);
      setTimeout(() => setBackupCopied(false), 1800);
    } catch {
      /* clipboard nedisponibil */
    }
  };

  const downloadBackupCodes = () => {
    const blob = new Blob([backupCodes.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tls-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copySecret = async () => {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard nedisponibil */
    }
  };

  const handleBack = () => {
    setError('');
    if (step === 'choose') return;
    if (step === 'setup') {
      setStep('choose');
      setMethod(null);
      return;
    }
    if (step === 'verify') {
      setStep('setup');
      setCode(EMPTY_CODE);
      return;
    }
  };

  return (
    <div className="auth-shell auth-shell-2fa auth-shell-embed">
      <div className="auth-2fa-wrap">
        <div className="auth-2fa-header">
          <div className="auth-2fa-logo">
            <BrandLogo className="auth-logo-img auth-logo-img-sm" />
            <div>
              <div className="auth-brand-sub" style={{ color: 'var(--text-muted)' }}>
                {t('sec.title')}
              </div>
            </div>
          </div>
          <div className="row gap-2" style={{ alignItems: 'center' }}>
            <span className={'badge ' + (activated ? 'badge-success' : 'badge-warn')}>
              <span className="dot" />
              {activated ? t('sec.status.active') : t('sec.status.inactive')}
            </span>
            {step !== 'done' && step !== 'choose' && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={handleBack}>
                <ArrowLeft size={13} />
                {isRo ? 'Înapoi' : 'Back'}
              </button>
            )}
          </div>
        </div>

        <ol className="tfa-steps">
          {stepsList.map((s, i) => {
            const activeIdx = stepsList.findIndex((x) => x.id === step);
            const state = i < activeIdx ? 'done' : i === activeIdx ? 'current' : 'pending';
            return (
              <li key={s.id} className={'tfa-step tfa-step-' + state}>
                <span className="tfa-step-dot">
                  {state === 'done' ? <Check size={12} strokeWidth={3} /> : i + 1}
                </span>
                <span className="tfa-step-label">{s.label}</span>
              </li>
            );
          })}
        </ol>

        <div className="auth-2fa-card">
          {step === 'choose' && (
            <>
              <div className="tfa-head">
                <h1 className="auth-title">{t('tfa.choose.title')}</h1>
                <p className="auth-sub">{t('tfa.choose.sub')}</p>
              </div>

              <div className="tfa-options">
                <button type="button" className="tfa-option" onClick={() => pickMethod('app')}>
                  <div className="tfa-option-ic">
                    <Smartphone size={22} strokeWidth={1.8} />
                  </div>
                  <div className="tfa-option-body">
                    <div className="tfa-option-title">
                      {t('tfa.choose.app.title')}
                      <span className="tfa-badge-rec">{t('tfa.choose.recommended')}</span>
                    </div>
                    <div className="tfa-option-sub">{t('tfa.choose.app.sub')}</div>
                  </div>
                  <ChevronRight size={16} />
                </button>

                <button type="button" className="tfa-option" onClick={() => pickMethod('email')}>
                  <div className="tfa-option-ic tfa-option-ic-alt">
                    <Mail size={22} strokeWidth={1.8} />
                  </div>
                  <div className="tfa-option-body">
                    <div className="tfa-option-title">{t('tfa.choose.email.title')}</div>
                    <div className="tfa-option-sub">{t('tfa.choose.email.sub')}</div>
                  </div>
                  <ChevronRight size={16} />
                </button>
              </div>

              <div className="tfa-info">
                <Info size={14} />
                <span>{t('tfa.choose.info')}</span>
              </div>
            </>
          )}

          {step === 'setup' && method === 'app' && (
            <>
              <div className="tfa-head">
                <h1 className="auth-title">{t('tfa.app.title')}</h1>
                <p className="auth-sub">{t('tfa.app.sub')}</p>
              </div>

              <div className="tfa-qr-row">
                <div className="tfa-qr-card">
                  {qrCode ? (
                    <img
                      src={qrCode}
                      alt="QR code"
                      width={180}
                      height={180}
                      style={{ display: 'block' }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 180,
                        height: 180,
                        display: 'grid',
                        placeItems: 'center',
                        color: 'var(--text-muted)',
                      }}
                    >
                      <RefreshCw
                        size={18}
                        style={{ animation: 'spin 1s linear infinite' }}
                      />
                    </div>
                  )}
                  <div className="tfa-qr-foot">
                    <Smartphone size={13} />
                    <span>{t('tfa.app.refresh_30')}</span>
                  </div>
                </div>

                <div className="tfa-manual">
                  <div className="tfa-manual-label">{t('tfa.app.manual_label')}</div>
                  <div className="tfa-manual-key">
                    <code>
                      {secret ? secret.match(/.{1,4}/g)?.join(' ') || secret : '— — — —'}
                    </code>
                    <button type="button" className="btn btn-sm" onClick={copySecret}>
                      {copied ? <Check size={13} /> : <Copy size={13} />}
                      {copied ? t('tfa.app.copied') : t('tfa.app.copy')}
                    </button>
                  </div>
                  <div className="tfa-manual-meta">
                    <div>
                      <span className="muted">{isRo ? 'Cont' : 'Account'}:</span> TLS Client Portal
                    </div>
                    <div>
                      <span className="muted">{isRo ? 'Algoritm' : 'Algorithm'}:</span> TOTP · SHA-1
                      · 6 digits · 30s
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="btn btn-primary auth-submit"
                onClick={() => {
                  setCode(EMPTY_CODE);
                  setError('');
                  setStep('verify');
                }}
              >
                {t('tfa.app.continue')}
                <ArrowRight size={13} strokeWidth={2.5} />
              </button>
            </>
          )}

          {step === 'setup' && method === 'email' && (
            <>
              <div className="tfa-head">
                <h1 className="auth-title">{t('tfa.email.title')}</h1>
                <p className="auth-sub">{t('tfa.email.sub')}</p>
              </div>

              <div className="tfa-email-block">
                <div className="tfa-email-ic">
                  <Mail size={22} strokeWidth={1.8} />
                </div>
                <div className="tfa-email-body">
                  <div className="tfa-email-label">{t('tfa.email.label')}</div>
                  <div className="tfa-email-value mono">{user?.email || '—'}</div>
                </div>
                <button
                  type="button"
                  className="link accent"
                  onClick={(e) => e.preventDefault()}
                >
                  {t('tfa.email.change')}
                </button>
              </div>

              <button
                type="button"
                className="btn btn-primary auth-submit"
                onClick={handleSendEmail}
                disabled={emailSending}
              >
                {emailSending ? (
                  <>
                    <RefreshCw
                      size={13}
                      strokeWidth={2.5}
                      style={{ animation: 'spin 0.8s linear infinite' }}
                    />
                    {t('tfa.email.sending')}
                  </>
                ) : (
                  <>
                    <Zap size={13} strokeWidth={2.5} />
                    {t('tfa.email.send')}
                  </>
                )}
              </button>
            </>
          )}

          {step === 'verify' && (
            <>
              <div className="tfa-head">
                <h1 className="auth-title">{t('tfa.verify.title')}</h1>
                <p className="auth-sub">
                  {method === 'email' ? t('tfa.verify.sub.email') : t('tfa.verify.sub.app')}
                </p>
              </div>

              <form onSubmit={handleVerify} className="tfa-verify-form">
                <div className="tfa-otp">
                  {code.map((d, i) => (
                    <input
                      key={i}
                      ref={(el) => {
                        refs.current[i] = el;
                      }}
                      inputMode="numeric"
                      maxLength={1}
                      value={d}
                      onChange={(e) => onCodeChange(i, e.target.value)}
                      onKeyDown={(e) => onCodeKey(i, e)}
                      onPaste={i === 0 ? onCodePaste : undefined}
                      autoFocus={i === 0}
                      className={'tfa-otp-cell' + (error ? ' err' : '') + (d ? ' filled' : '')}
                      aria-label={`Digit ${i + 1}`}
                    />
                  ))}
                </div>

                {error && (
                  <div className="tfa-error">
                    <AlertTriangle size={13} />
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary auth-submit"
                  disabled={verifying}
                >
                  {verifying ? (
                    <>
                      <RefreshCw
                        size={13}
                        strokeWidth={2.5}
                        style={{ animation: 'spin 0.8s linear infinite' }}
                      />
                      {t('tfa.verify.btn_loading')}
                    </>
                  ) : (
                    <>
                      <ShieldCheck size={13} strokeWidth={2.5} />
                      {t('tfa.verify.btn')}
                    </>
                  )}
                </button>

                <div className="tfa-resend">
                  {method === 'email' ? (
                    resendIn > 0 ? (
                      <span className="muted">
                        {t('tfa.verify.resend_in').replace('{s}', String(resendIn))}
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="link accent"
                        onClick={handleResendEmail}
                      >
                        {t('tfa.verify.resend')}
                      </button>
                    )
                  ) : (
                    <span className="muted">{t('tfa.verify.app_30')}</span>
                  )}
                </div>
              </form>
            </>
          )}

          {step === 'done' && (
            <>
              <div className="tfa-success-head">
                <div className="tfa-success-badge">
                  <ShieldCheck size={32} strokeWidth={2} />
                </div>
                <h1 className="auth-title">{t('tfa.done.title.app')}</h1>
                <p className="auth-sub">
                  {method === 'email' ? t('tfa.done.sub.email') : t('tfa.done.sub.app')}
                </p>
              </div>

              <div className="tfa-backup">
                <div className="tfa-backup-head">
                  <div>
                    <div className="tfa-backup-title">
                      <Key size={14} />
                      {t('tfa.backup.title')}
                    </div>
                    <div className="muted fs-12 mt-1">{t('tfa.backup.sub')}</div>
                  </div>
                </div>
                <div className="tfa-backup-grid">
                  {backupCodes.map((c) => (
                    <div key={c} className="tfa-backup-code mono">
                      {c}
                    </div>
                  ))}
                </div>
                <div className="row gap-2" style={{ marginTop: 12, flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn-sm" onClick={copyBackupCodes}>
                    {backupCopied ? <Check size={13} /> : <Copy size={13} />}
                    {backupCopied ? t('tfa.app.copied') : t('tfa.backup.copy_all')}
                  </button>
                  <button type="button" className="btn btn-sm" onClick={downloadBackupCodes}>
                    <Download size={13} />
                    {t('tfa.backup.download')}
                  </button>
                </div>
              </div>

              <button
                type="button"
                className="btn btn-primary auth-submit"
                onClick={() => {
                  setStep('choose');
                  setMethod(null);
                  setCode(EMPTY_CODE);
                }}
              >
                {t('tfa.done.go')}
                <ArrowRight size={13} strokeWidth={2.5} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwoStepConfigure;
